import React from 'react';
import moment from 'moment';
import axios from 'axios';
import qs from 'qs';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import LaunchIcon from '@material-ui/icons/Launch';
import EditIcon from '@material-ui/icons/Edit';
import EntryNoteContentDialog from './Timetable/EntryNoteContentDialog';
import EntryPupilNoteContentDialog
  from './Timetable/EntryPupilNoteContentDialog';

const styles = () => ({
  wrapper: {
    borderBottom: '1px solid #ccc',
    color: '#797979',
    padding: 20,
  },
  wrapperBreak: {
    backgroundColor: '#edf2f5',
    color: '#7f8fa4',
  },
  wrapperActive: {
    backgroundColor: '#2096f3',
    color: '#fff',
    '& a': {
      color: '#ffffff',
      textDecoration: 'underline',
    },
  },
  noteHead: {
    fontSize: 14,
  },
  note: {
    paddingTop: 5,
    fontSize: 14,
    wordWrap: 'break-word',
    overflow: 'hidden',
    cursor: 'pointer',
  },
  noteShown: {
    paddingTop: 10,
    fontSize: 14,
    wordWrap: 'break-word',
  },
  showIcon: {
    color: '#f56a12',
  },
  hideIcon: {
    visibility: 'hidden',
    display: 'none',
  },
  showAllLabel: {
    fontSize: '14px',
    textAlign: 'right',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  toggleButton: {
    minWidth: 7,
    padding: 5,
    color: '#f56a12',
  },
  toggleButtonWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
  },
  toggleButtonViewPupilNote: {
    marginLeft: 7,
  },
  noPaddingTop: {
    paddingTop: '0px !important',
  },
  darkGreyFont: {
    color: '#999999',
  },
  whiteFont: {
    color: '#ffffff',
  },
});

class TimetableEntry extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expandTeacherNote: false,
      expandPupilNote: false,
      editPupilNote: false,
    };

    this.handleToggleExpandNote = this.handleToggleExpandNote.bind(this);
    this.handlePupilNoteSubmit = this.handlePupilNoteSubmit.bind(this);
    this.handleToggleEditPupilNote = this.handleToggleEditPupilNote.bind(this);
  }

  handleToggleExpandNote(teacher) {
    this.setState(teacher ? {
      expandTeacherNote: !this.state.expandTeacherNote,
    } : {
      expandPupilNote: !this.state.expandPupilNote,
    });
  }

  handleToggleEditPupilNote() {
    this.setState({
      editPupilNote: !this.state.editPupilNote,
    });
  }

  handleStopLinkPropagation(e) {
    if (e.target.tagName === 'A') {
      e.stopPropagation();
    }
  }

  handlePupilNoteSubmit(note) {
    const {
      data: timetableEntry,
    } = this.props;

    return axios
      .post(
        `${window.apiUrl}/savetimetablenote/${timetableEntry.key}`,
        qs.stringify({
          note,
        }),
        {
          cancelToken: new axios.CancelToken((c) => {
            this.axiosCancel = c
          }),
        }
      )
      .then((response) => {
        if (!response.data.success) {
          return;
        }

        this.props.data.pupil_note_abstract = response.data.data.abstract;
        this.props.data.pupil_note = response.data.data.note;
        this.props.data.pupil_note_raw = response.data.data.raw_note;

        window.dispatchEvent(new CustomEvent(
          'timetable:save-note',
          {
            bubbles: true,
            detail: {},
          }
        ));
      })
      .catch((thrown) => {
      });
  }

  formatNote(note) {
    let html = note.replace(/target="[a-z_]+"/g, '');
    if (typeof isIosApp !== 'undefined' && isIosApp) {
      html = html.replace(/<a\s+href/g, '<a target="_system" href');
    } else {
      html = html.replace(/<a\s+href/g, '<a target="_blank" href');
    }

    return html;
  }

  formatNoteAbstract(note) {
    let noteLength = this.props.data.pupil_note.length;
    return this.formatNote(note) + (
      noteLength > this.props.timetableNoteAbstractLength ? this.props.ellipsis : ''
    );
  }

  render() {
    const {
      classes,
      data: timetableEntry,
      t,
      allowAddTimetableNotes,
    } = this.props;

    let wrapperClassName = classes.wrapper;
    if (timetableEntry.is_break) {
      wrapperClassName = `${classes.wrapper} ${classes.wrapperBreak}`;
    }

    const timeCurrent = moment();
    const timeFrom = moment(timetableEntry.start_time);
    const timeTo = moment(timetableEntry.end_time);
    const isNow = timeCurrent.isBetween(timeFrom, timeTo);

    if (isNow) {
      wrapperClassName = `${classes.wrapper} ${classes.wrapperActive}`;
    }

    const teacherNoteHtml = this.formatNote(timetableEntry.note);
    const pupilNoteHtml = this.formatNote(timetableEntry.pupil_note);

    const teacherNoteAbstractHtml = this.formatNoteAbstract(timetableEntry.note_abstract);
    const pupilNoteAbstractHtml = this.formatNoteAbstract(timetableEntry.pupil_note_abstract);

    let buttons = '';
    if (appType === 'student') {
      buttons = pupilNoteHtml
      ? (
        <Button
          onClick={this.handleToggleEditPupilNote}
          className={`toggle-edit-pupil-button ${classes.toggleButton} ${isNow ? classes.whiteFont : ''}`}
        >
          <EditIcon className={`${classes.showIcon} ${isNow ? classes.whiteFont : ''}`}/>
        </Button>
      )
      : (
        <Button
          onClick={this.handleToggleEditPupilNote}
          variant="contained"
          color="secondary"
          className="edit-note-button"
        >
          <small>{timetableEntry.pupil_note ? t('Edit') : t('Add Note')}</small>
        </Button>
      );
    }

    return (
      <div className={`timetable-entry ${wrapperClassName}`}>
        <Grid
          justify="space-between"
          container
          spacing={4}
        >
          <Grid item xs={6} md={3}>
            {timeFrom.format('HH:mm')}
            <span> - </span>
            {timeTo.format('HH:mm')}
          </Grid>
          <Grid item xs={6} md={9}>
            <div>
              <b>{timetableEntry.lesson_name}</b>
              {timetableEntry.subject_name ? ' (' + timetableEntry.subject_name + ')' : ''}
              {timetableEntry.is_alternative_lesson ? ' (Priority)' : ''}
              <br/>
              <span className={`${classes.darkGreyFont} ${isNow ? classes.whiteFont : ''}`}>{timetableEntry.teacher_name} | {timetableEntry.room_name}</span>
            </div>
          </Grid>
          {timetableEntry.note && (
            <Grid item xs={12} md={allowAddTimetableNotes ? 6 : 12} className={classes.noPaddingTop}>
              <div className={classes.noteHead}>
                <b>{t('Teacher Note')}</b>
              </div>
              <div onClick={this.handleToggleExpandNote.bind(this, true)}>
                <div
                  onClick={(e) => this.handleStopLinkPropagation(e)}
                  className={classes.note}
                  dangerouslySetInnerHTML={{
                    __html: teacherNoteAbstractHtml,
                  }}
                ></div>
              </div>
              <EntryNoteContentDialog
                isDialogOpen={this.state.expandTeacherNote}
                onDialogClose={this.handleToggleExpandNote.bind(this, true)}
                note={teacherNoteHtml}
                useSuspense={false}
              />
              <div className={classes.showAllLabel}>
                {teacherNoteHtml.length > this.props.timetableNoteAbstractLength && (
                  <Button
                    onClick={this.handleToggleExpandNote.bind(this, true)}
                    className={`toggle-expand-note-button ${classes.toggleButton} ${isNow ? classes.whiteFont : ''}`}
                  >
                    <LaunchIcon className={`${classes.showIcon} ${isNow ? classes.whiteFont : ''}`}/>
                  </Button>
                )}
              </div>
            </Grid>
          )}
          {allowAddTimetableNotes && (
            <Grid item xs={12} md={timetableEntry.note ? 6 : 12} className={classes.noPaddingTop}>
              {pupilNoteHtml && (
                <div className={classes.noteHead}>
                  <b>{appType === 'student' ? t('Your Note') : t('Pupil Note')}</b>
                </div>
              )}
              {timetableEntry.pupil_note && (
                <div onClick={this.handleToggleExpandNote.bind(this, false)}>
                  <div
                    onClick={(e) => this.handleStopLinkPropagation(e)}
                    className={classes.note}
                    dangerouslySetInnerHTML={{
                      __html: pupilNoteAbstractHtml,
                    }}
                  ></div>
                </div>
              )}
              {appType === 'student' && (
                <EntryPupilNoteContentDialog
                  timetableEntryName={timetableEntry.lesson_name}
                  isDialogOpen={this.state.editPupilNote}
                  onDialogClose={this.handleToggleEditPupilNote}
                  onSubmit={this.handlePupilNoteSubmit}
                  note={timetableEntry.pupil_note_raw}
                  useSuspense={false}
                />
              )}
              <EntryNoteContentDialog
                isDialogOpen={this.state.expandPupilNote}
                onDialogClose={this.handleToggleExpandNote.bind(this, false)}
                note={pupilNoteHtml}
                useSuspense={false}
              />
              {(timetableEntry.pupil_note_raw.length > this.props.timetableNoteAbstractLength || appType === 'student') && (
                <div className={classes.showAllLabel}>
                  {buttons}
                  {timetableEntry.pupil_note_raw.length > this.props.timetableNoteAbstractLength && (
                    <Button
                      onClick={this.handleToggleExpandNote.bind(this, false)}
                      className={`toggle-expand-note-button ${classes.toggleButton} ${classes.toggleButtonViewPupilNote} ${isNow ? classes.whiteFont : ''}`}
                    >
                      <LaunchIcon className={`${classes.showIcon} ${isNow ? classes.whiteFont : ''}`}/>
                    </Button>
                  )}
                </div>
              )}
            </Grid>
          )}
        </Grid>
      </div>
    );
  }
}

export default withStyles(styles)(withTranslation()(TimetableEntry));
